import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import { figmaTokens } from "assets/theme/designTokens";
import { useTranslate } from "utils/i18n";

function summaryEntries(summary) {
  if (!summary || typeof summary !== "object") return [];
  const skip = new Set(["title", "Title"]);
  return Object.entries(summary)
    .filter(([k, v]) => !skip.has(k) && v != null && v !== "")
    .map(([key, value]) => {
      if (typeof value === "object") {
        const label =
          value.name ?? value.Name ?? value.fullName ?? value.FullName ?? value.regNumber ?? value.RegNumber ?? null;
        const secondary = value.regNumber ?? value.RegNumber ?? value.id ?? value.Id ?? null;
        return {
          key,
          label: key,
          value: label != null ? String(label) : JSON.stringify(value),
          secondary: secondary != null && String(secondary) !== String(label) ? String(secondary) : null,
        };
      }
      return { key, label: key, value: String(value), secondary: null };
    });
}

function prettyLabel(key, language) {
  const map =
    language === "en"
      ? {
          teacher: "Teacher",
          establishment: "Establishment",
          assignmentDate: "Date",
          leaveRequest: "Leave request",
          leaveRequestId: "Leave #",
          leaveType: "Leave type",
          leaveTypeId: "Leave type #",
          startDate: "Start",
          endDate: "End",
          reason: "Reason",
          status: "Status",
          license: "License",
        }
      : {
          teacher: "Enseignante",
          establishment: "Établissement",
          assignmentDate: "Date",
          leaveRequest: "Demande de congé",
          leaveRequestId: "Congé #",
          leaveType: "Type de congé",
          leaveTypeId: "Type #",
          startDate: "Début",
          endDate: "Fin",
          reason: "Motif",
          status: "Statut",
          license: "Licence",
        };
  return map[key] || key;
}

/**
 * Confirmation card for Assistant command preview → execute.
 */
export default function CommandConfirmCard({ confirm, onAction, disabled }) {
  const { language } = useTranslate();
  const fr = language !== "en";
  const [now, setNow] = useState(Date.now());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const expiresAt = confirm?.expiresAt || 0;
  const secondsLeft = Math.max(0, Math.ceil((expiresAt - now) / 1000));
  const expired = secondsLeft <= 0;
  const locked = Boolean(disabled || busy || confirm?.consumed || expired);

  const title =
    confirm?.summary?.title ||
    confirm?.summary?.Title ||
    (fr ? "Confirmer l'action" : "Confirm action");

  const entries = useMemo(() => summaryEntries(confirm?.summary), [confirm?.summary]);
  const warnings = Array.isArray(confirm?.warnings) ? confirm.warnings : [];

  const handleConfirm = () => {
    if (locked) return;
    setBusy(true);
    onAction?.({
      action: "command-execute",
      commandConfirm: confirm,
    });
  };

  const handleCancel = () => {
    onAction?.({ action: "command-cancel" });
  };

  return (
    <SoftBox
      mt={1.25}
      sx={{
        borderRadius: "16px",
        border: `1px solid ${figmaTokens.borderSubtle}`,
        overflow: "hidden",
        boxShadow: "0 10px 28px rgba(15, 23, 42, 0.08)",
        bgcolor: figmaTokens.surface,
      }}
    >
      <SoftBox
        px={1.75}
        py={1.35}
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
          background: "linear-gradient(135deg, rgba(240, 177, 0, 0.16) 0%, rgba(102, 126, 234, 0.08) 100%)",
          borderBottom: `1px solid ${figmaTokens.borderSubtle}`,
        }}
      >
        <SoftBox
          width={34}
          height={34}
          borderRadius="10px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{ bgcolor: "rgba(240, 177, 0, 0.2)" }}
        >
          <Icon sx={{ color: "#b45309" }}>gavel</Icon>
        </SoftBox>
        <SoftBox flex={1} minWidth={0}>
          <SoftTypography variant="button" fontWeight="bold" sx={{ color: figmaTokens.textPrimary }}>
            {title}
          </SoftTypography>
          <SoftTypography variant="caption" display="block" sx={{ color: figmaTokens.textMuted }}>
            {confirm?.command}
            {" · "}
            {expired
              ? fr
                ? "Jeton expiré — relancez la commande"
                : "Token expired — run the command again"
              : fr
                ? `Expire dans ${secondsLeft}s`
                : `Expires in ${secondsLeft}s`}
          </SoftTypography>
        </SoftBox>
      </SoftBox>

      <SoftBox px={1.75} py={1.35} display="flex" flexDirection="column" gap={0.85}>
        {entries.map((row) => (
          <SoftBox
            key={row.key}
            display="flex"
            justifyContent="space-between"
            gap={1.5}
            sx={{
              py: 0.6,
              borderBottom: `1px dashed ${figmaTokens.borderSubtle}`,
            }}
          >
            <SoftTypography variant="caption" fontWeight="bold" sx={{ color: figmaTokens.textMuted, textTransform: "uppercase", letterSpacing: "0.03em" }}>
              {prettyLabel(row.label, language)}
            </SoftTypography>
            <SoftBox textAlign="right" minWidth={0}>
              <SoftTypography variant="button" fontWeight="medium" sx={{ fontSize: "0.8rem", color: figmaTokens.textPrimary }}>
                {row.value}
              </SoftTypography>
              {row.secondary && (
                <SoftTypography variant="caption" display="block" sx={{ color: figmaTokens.textMuted }}>
                  {row.secondary}
                </SoftTypography>
              )}
            </SoftBox>
          </SoftBox>
        ))}

        {warnings.map((w) => (
          <SoftBox
            key={String(w)}
            display="flex"
            gap={0.75}
            alignItems="flex-start"
            sx={{
              p: 1,
              borderRadius: "10px",
              bgcolor: "rgba(240, 177, 0, 0.1)",
              border: "1px solid rgba(240, 177, 0, 0.35)",
            }}
          >
            <Icon sx={{ fontSize: "1rem !important", color: "#b45309", mt: "1px" }}>warning</Icon>
            <SoftTypography variant="caption" sx={{ color: "#92400e", fontWeight: 600 }}>
              {w}
            </SoftTypography>
          </SoftBox>
        ))}
      </SoftBox>

      <SoftBox
        px={1.75}
        py={1.25}
        display="flex"
        flexWrap="wrap"
        gap={1}
        sx={{ borderTop: `1px solid ${figmaTokens.borderSubtle}`, bgcolor: figmaTokens.pageBg }}
      >
        <SoftButton
          variant="gradient"
          color="info"
          size="small"
          disabled={locked}
          onClick={handleConfirm}
          sx={{ minWidth: 120 }}
        >
          {busy ? (fr ? "Exécution…" : "Executing…") : fr ? "Confirmer" : "Confirm"}
        </SoftButton>
        <SoftButton variant="outlined" color="secondary" size="small" disabled={busy} onClick={handleCancel}>
          {fr ? "Annuler" : "Cancel"}
        </SoftButton>
        {expired && (
          <Chip
            size="small"
            label={fr ? "Expiré" : "Expired"}
            sx={{ height: 28, fontWeight: 700, bgcolor: "rgba(239, 68, 68, 0.1)", color: "#b91c1c" }}
          />
        )}
      </SoftBox>
    </SoftBox>
  );
}

CommandConfirmCard.propTypes = {
  confirm: PropTypes.shape({
    command: PropTypes.string,
    confirmToken: PropTypes.string,
    summary: PropTypes.object,
    warnings: PropTypes.array,
    expiresAt: PropTypes.number,
    consumed: PropTypes.bool,
  }),
  onAction: PropTypes.func,
  disabled: PropTypes.bool,
};
